import { Formik } from "formik";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  FormLabel,
} from "@mui/material";
import ClientNameDetails from "../../common/form-components/ClientNameDetails";
import ClientAddressDetails from "../../common/form-components/ClientAddressDetails";
import AlertRibbon from "../../../common/AlertRibbon";
import countryList from "country-list";
import ProductsSelect from "../../common/form-components/ProductsSelect";
import {
  ImageUploader,
  ImageUploadProps,
} from "../../common/form-components/ImageUploader";
import PricingPlanSelect from "../../common/form-components/PricingPlanSelect";
import BillingIntervalSelect from "../../common/form-components/BillingIntervalSelect";
import IsPrivateLedgerCheckBox from "../../common/form-components/IsPrivateLedgerCheckBox";
import {
  ClientDetails,
  useClientDetails,
} from "../../context/clientDetailsContext";
import { getEditClientForm } from "./formSchema";

function EditClientDetails() {
  const {
    client,
    updateClientDetails: {
      isEditing,
      setEditing,
      update,
      loading,
      alert,
      setAlert,
    },
  } = useClientDetails();

  if (!client?.details) {
    throw Error("Client details not found");
  }

  const addRequiredProperties = (details: ClientDetails) => {
    const requiredProps = [
      "id",
      "name",
      "products",
      "pricingPlan",
      "isPrivateLedger",
      "address",
    ] as const;

    return requiredProps.find((prop) => prop in details || "");
  };

  if (!addRequiredProperties(client.details)) {
    throw Error("Missing required client properties");
  }

  const {
    id,
    imageUrl,
    name,
    products,
    pricingPlan,
    isPrivateLedger,
    address,
  } = client.details;

  const clientDetailsInitialValues = {
    "image-url": imageUrl,
    "client-name": name,
    "client-products": products,
    "client-pricing-plan": pricingPlan?.level,
    "client-billing-interval": pricingPlan?.billingInterval || "",
    "is-private-ledger": !!isPrivateLedger,
    "address-street": address.street,
    "address-city": address.city,
    "address-zip-code": address.zipCode,
    "country-select":
      countryList
        .getData()
        .find((country) => country.code === address.country) || null,
    id,
  };

  const mainUser = client.details.mainUser;

  return (
    <>
      <Formik
        initialValues={clientDetailsInitialValues}
        validationSchema={getEditClientForm(
          !!mainUser?.stripeUrl,
          !!mainUser?.hubspotUrl,
        )}
        onSubmit={(form) => update(form)}
        validateOnMount
        initialTouched={{
          "client-pricing-plan": true,
          "client-billing-interval": true,
          "client-products": true,
          "client-name": true,
          "is-private-ledger": false,
          "address-zip-code": true,
          "address-city": true,
          "country-select": true,
        }}
      >
        {({ handleSubmit, isValid, values, resetForm }) => {
          const ImageUploadProp: ImageUploadProps = {
            id: values.id as string,
            imageUrl: imageUrl,
            width: "100%",
            height: "100%",
            editPage: true,
            // 2MB is the current setting in bytes (1MB = 1048576bytes)
            maxFileSize: 2 * 1048576,
          };
          return (
            <>
              <Dialog
                data-testid="client-edit-modal"
                open={isEditing}
                maxWidth="md"
              >
                <DialogTitle
                  sx={{
                    lineHeight: "1rem",
                    marginBottom: 0,
                    paddingTop: "1.5rem",
                    paddingBottom: "0.1rem",
                  }}
                >
                  Edit Client
                </DialogTitle>
                <Box component="form" onSubmit={handleSubmit}>
                  <DialogContent>
                    {alert && alert.type === "error" && (
                      <AlertRibbon
                        handleClose={() => setAlert(null)}
                        severity={alert.type}
                        message={alert.message}
                        errorCode={alert.errorCode}
                      />
                    )}
                    <FormGroup
                      sx={{
                        paddingBottom: 2,
                        float: "right",
                        paddingTop: 1,
                        position: "relative",
                        top: "-1vh",
                        width: "70%",
                        zIndex: 2,
                      }}
                    >
                      <FormLabel sx={{ paddingBottom: 1 }}>Details</FormLabel>
                      <ClientNameDetails />
                      <PricingPlanSelect />
                      <BillingIntervalSelect />
                      <ProductsSelect />
                      <IsPrivateLedgerCheckBox />
                    </FormGroup>
                    <Box
                      sx={{
                        position: "relative",
                        top: "0",
                        left: "0",
                        float: "left",
                        width: "25%",
                        aspectRatio: "1/1",
                        display: "flex",
                        margin: "0.5rem",
                      }}
                    >
                      <ImageUploader {...ImageUploadProp} />
                    </Box>
                    <FormGroup
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <FormLabel sx={{ paddingBottom: 1 }}>Address</FormLabel>
                      <ClientAddressDetails />
                    </FormGroup>
                  </DialogContent>

                  <DialogActions>
                    <Button
                      onClick={() => {
                        setAlert(null);
                        setEditing(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      loading={loading}
                      disabled={!isValid}
                      type="submit"
                      variant="contained"
                    >
                      Save
                    </Button>
                  </DialogActions>
                </Box>
              </Dialog>
            </>
          );
        }}
      </Formik>
    </>
  );
}

export default EditClientDetails;
